import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor';
import type {
  AlineMarkdownEditorProps,
  AlineMarkdownEditorRef,
} from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor';

import { normalizeEditorMarkdown } from './skill-editor-content';

export interface SkillMarkdownEditorRef {
  /** Report an edit still waiting in the editor's debounce now, for a save or a Run. */
  flush: () => void;
}

type SkillMarkdownEditorProps = Omit<AlineMarkdownEditorProps, 'onChange'> & {
  /** Called with the Markdown to store, only when the author changed what the file says. */
  onChange: (markdown: string) => void;
};

/**
 * The rich-text editor for a skill's Markdown files, reporting edits only.
 *
 * The editor's Markdown round trip is not the identity: it turns `-` lists into `*`,
 * `*x*` into `_x_`, and adds an empty paragraph after a trailing heading. It also reports
 * a change when it loads its value and whenever the selection moves. Passed straight to
 * the page, any of those would rewrite a file its author only opened, or leave a read-only
 * viewer with "unsaved" changes. So what the editor shows once it has loaded is the
 * baseline, and only Markdown that differs from what was last reported is passed on.
 */
export const SkillMarkdownEditor = React.memo(
  forwardRef<SkillMarkdownEditorRef, SkillMarkdownEditorProps>(function SkillMarkdownEditor(
    { disabled = false, onChange, readOnly = false, ...props },
    ref
  ) {
    const editorRef = useRef<AlineMarkdownEditorRef>(null);
    const reportedRef = useRef<string | null>(null);

    /**
     * The editor loads its value in its own mount effect, which runs before this one, so
     * this reads the file as the editor shows it. Taken again when an Activity pane is shown
     * (the editor reloads its value then), which keeps it in step with what is on screen.
     */
    useEffect(() => {
      const markdown = editorRef.current?.getMarkdown();
      if (markdown !== undefined) reportedRef.current = normalizeEditorMarkdown(markdown);
    }, []);

    const report = useCallback(
      (markdown: string) => {
        if (disabled || readOnly || reportedRef.current === null) return;
        const next = normalizeEditorMarkdown(markdown);
        if (next === reportedRef.current) return;
        reportedRef.current = next;
        onChange(next);
      },
      [disabled, onChange, readOnly]
    );

    useImperativeHandle(
      ref,
      () => ({
        flush: () => {
          const markdown = editorRef.current?.getMarkdown();
          if (markdown !== undefined) report(markdown);
        },
      }),
      [report]
    );

    return (
      <AlineMarkdownEditor
        ref={editorRef}
        disabled={disabled}
        readOnly={readOnly}
        onChange={report}
        {...props}
      />
    );
  })
);
